import './ChannelPlatformTacticBrand.scss';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import {
  capitalizeWords,
  cptbAreaData,
  currencyFormatter,
  snapShotDataSort
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { AreaChart } from '@Components/Charts/AreaChart';
import { LineChart } from '@Components/Charts/LineChart';
import {
  calculateTTMOverallRoasCpa,
  cptbSnapShotData,
  cptbTwoStageSnapData
} from '@Components/Charts/ModelResults/DataFormatters';
import { ChartContainer } from '@Components/ChartContainer';
import { WidgetContainer } from '@Components/WidgetContainer';
import { SnapshotChart } from '@Components/SnapshotChart/SnapshotChart';
import { DatePicker } from '@Components/DatePicker';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Design, Placement, Size, StretchMode } from '@Components/designConstants';
import IncrementalityIcon from '../../../assets/IncrementalityIcon.svg?react';
import { DateRange, dateToMddyy, getDefaultDates } from '../../../utils/date-utils';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { Neutral400 } from '../../../utils/colors';
import { useLocationCompany } from '../../../utils/hooks/useLocationCompany';
import { CONVERSION_FORMATTER_TYPE, CONVERSION_PRETTY_NAME } from '../mmm-utils';
import { DATE_GROUPING_OPTIONS } from './model-results-constants';
import { StackedAreaChart } from './StackedAreaChart';
import { TwoStageSpendandEffectContainer } from './TwoStageSpendAndEffectContainer';

// Minimum period spend (in $) below which a per-period ROAS/CPA is forced to 0 to suppress
// lag-induced spikes on near-zero-spend periods (DEV-21247). Separate from HROAS's
// MIN_PERIOD_SPEND_FOR_ROAS (roasPipeline.ts) since the two products tune it independently.
const MIN_PERIOD_SPEND_FOR_ROAS = 100;

const DEFAULT_SORT_SELECTIONS: Record<string, string> = {
  spendAndEffectShare: 'Spend: High to low',
  revenue: 'Highest to lowest',
  costPerAcquisition: 'Highest to lowest',
  roas: 'Highest to lowest'
};

const DEFAULT_SORT_OPTIONS = [
  { value: 'Alphabetical' },
  { value: 'Highest to lowest' },
  { value: 'Lowest to highest' }
];

interface ChannelPlatformTacticBrandProps {
  cptbData: any[];
  disabledPlatform: boolean | undefined;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  kpiType: string;
  setOverallRoasCpa: Dispatch<SetStateAction<number | undefined>>;
  showActualizedData: boolean;
  twoStageData?: any[];
  kpi: string;
  modelDate: string;
}

const HARD_CODED_COLOR = Neutral400;
const HARD_CODED_COLOR_NAME = 'Covariates';

const WIDGET_SUBHEADER: Record<string, ReactNode> = {
  revenue: (
    <>
      Attribution of revenue to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  costPerAcquisition: (
    <>
      Attribution of cost-pers to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) /
        ((level of media investment at that point in time) x (model parameter estimates for that
        media input)).
      </InfoTooltip>
    </>
  ),
  roas: (
    <>
      Attribution of ROAS to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as ((level of media investment at that point in time) x
        (model parameter estimates for that media input)) / (level of media investment at that point
        in time).
      </InfoTooltip>
    </>
  ),
  spendAndEffectShare: (
    <>
      Attribution of spend and effect share to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Effect share calculated for each point in time as ((level of media investment at that point
        in time) x (model parameter estimates for that media input)) / (total KPI volume).
      </InfoTooltip>
    </>
  )
};

export const ChannelPlatformTacticBrand = ({
  cptbData,
  disabledPlatform,
  groupBy,
  setGroupBy,
  groupByOptions,
  kpiType,
  setOverallRoasCpa,
  showActualizedData,
  twoStageData,
  kpi,
  modelDate
}: ChannelPlatformTacticBrandProps) => {
  const [investment, setInvestment] = useState('spendAndEffectShare');
  const [dateGrouping, setDateGrouping] = useState('Week');
  const [dates, setDates] = useState({ start: '', end: '' });
  const [maxCptbDate, setMaxCptbDate] = useState<Date>();
  const [covariates, setCovariates] = useState(false);
  const [areaChartData, setAreaChartData] = useState({} as any);
  const [areaChart2Data, setAreaChart2Data] = useState({} as any);
  const [snapChartData, setSnapChartData] = useState([] as any);
  const [toggleLabel, setToggleLabel] = useState(false);
  const [snapChartSortValue, setSnapChartSortValue] = useState('Spend: High to low');
  const [absoluteOrRelative, setAbsoluteOrRelative] = useState('Absolute');
  const [twoStageBarData, setTwoStageBarData] = useState<any[]>();
  const company = useLocationCompany() ?? '';
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const SNAPCHART_TITLE: Record<string, string> = {
    spendAndEffectShare: 'Spend and Effect Share',
    revenue: `${CONVERSION_PRETTY_NAME[kpiType]} Decomposition Share`,
    costPerAcquisition: 'Average CPA',
    roas: 'Average ROAS'
  };

  const INVESTMENT_OPTIONS = useMemo(
    () => [
      { label: 'Spend and Effect Share', value: 'spendAndEffectShare', title: 'Spend and Effect' },
      {
        label: CONVERSION_PRETTY_NAME[kpiType],
        value: 'revenue',
        title: `${CONVERSION_PRETTY_NAME[kpiType]} Decomposition`
      },
      {
        label: 'Cost Per Acquisition',
        value: 'costPerAcquisition',
        title: 'Cost Per Acquisition'
      },
      { label: 'ROAS', value: 'roas', title: 'ROAS' }
    ],
    [kpiType]
  );

  const SPEND_AND_EFFECT_SORT_OPTIONS = [
    { value: 'Alphabetical' },
    { value: 'Spend to effect ratio' },
    { value: 'Spend: High to low' },
    { value: 'Spend: Low to high' },
    { value: `${CONVERSION_PRETTY_NAME[kpiType]}: High to low` },
    { value: `${CONVERSION_PRETTY_NAME[kpiType]}: Low to high` }
  ] as const;

  const exportFormattedData = useMemo(() => {
    if (!showActualizedData && cptbData && !R.isEmpty(snapChartData)) {
      const refreshDate = cptbData.length ? cptbData[0].refresh_date || '' : '';

      return snapChartData.barData.map(obj => {
        const sharedValues = {
          company,
          kpi,
          refresh_date: refreshDate,
          media_name: obj.name
        };
        switch (investment) {
          case 'revenue':
            return {
              ...sharedValues,
              decomposition_percent: obj.value,
              decomposition_value: obj.labelRawNumber
            };
          case 'costPerAcquisition':
            return {
              ...sharedValues,
              average_cpa: obj.value,
              efficiency_ratio_to_overall_cpa: obj.labelRawNumber
            };
          case 'roas':
            return {
              ...sharedValues,
              average_roas: obj.value,
              efficiency_ratio_to_overall_roas: obj.labelRawNumber
            };
          default:
            return {
              ...sharedValues,
              spend_share_percent: obj.value,
              effect_share_percent: obj.value2,
              spend_volume: obj.spend_volume,
              effect_volume: obj.effect_volume
            };
        }
      });
    }
    if (showActualizedData && twoStageData && twoStageBarData) {
      const refreshDate = twoStageData.length ? twoStageData[0].refresh_date || '' : '';

      return twoStageBarData.map(obj => ({
        company,
        kpi,
        refresh_date: refreshDate,
        media_name: obj.name,
        spend_actualized_percent: obj.totalActualizedSpend,
        effect_actualized_percent: obj.totalActualizedEffect,
        spend_actualized_volume: obj.totalActualizedSpendRawValue,
        effect_actualized_volume: obj.totalActualizedEffectRawValue
      }));
    }
    return [];
  }, [
    showActualizedData,
    snapChartData,
    twoStageBarData,
    company,
    kpi,
    cptbData,
    twoStageData,
    investment
  ]);

  useEffect(() => {
    if (twoStageData) {
      if (!R.isEmpty(twoStageData) && (dates.start === '' || dates.end === '')) {
        return setDates(getDefaultDates(twoStageData));
      }

      if (dates.start.length && dates.end.length) {
        const dateFilteredData = twoStageData.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate >= new Date(dates.start) && itemDate <= new Date(dates.end);
        });

        const formattedData = cptbTwoStageSnapData(
          dateFilteredData,
          disabledPlatform,
          CONVERSION_PRETTY_NAME[kpiType],
          kpiType,
          covariates,
          groupBy
        );

        // twoStageData comes from a separate endpoint that may lack incrementality_test.
        // Cross-reference with cptbData (which always has it) by channel name.
        const incrementalityChannels = new Set(
          cptbData
            .filter((item: any) => item.incrementality_test)
            .map((item: any) => (item.channel || '').toLowerCase())
        );
        const enrichedBarData = formattedData.barData.map((item: any) => ({
          ...item,
          incrementality:
            item.incrementality ||
            incrementalityChannels.has((item.channelName || item.name || '').toLowerCase())
        }));

        setTwoStageBarData(snapShotDataSort(enrichedBarData, snapChartSortValue));
      }
    }
    return undefined;
  }, [
    twoStageData,
    cptbData,
    disabledPlatform,
    kpiType,
    snapChartSortValue,
    showActualizedData,
    dates,
    covariates,
    groupBy
  ]);

  useEffect(() => {
    if (showActualizedData) {
      setTwoStageBarData(d => snapShotDataSort(d || [], snapChartSortValue));
    }
  }, [snapChartSortValue, showActualizedData, groupBy]);

  useEffect(() => {
    if (!R.isEmpty(cptbData)) {
      const dateRange = getDefaultDates(cptbData);
      setMaxCptbDate(Dfns.parseISO(dateRange.end));
    }
  }, [cptbData, setMaxCptbDate]);

  useEffect(() => {
    let chartData;

    if (!R.isEmpty(cptbData) && (dates.start === '' || dates.end === '')) {
      return setDates(getDefaultDates(cptbData));
    }

    if (!R.isEmpty(cptbData) && dates.start !== '' && dates.end !== '') {
      let dateRangeFilteredData = cptbData.filter((item: any) => {
        const itemDate = new Date(item.date);
        return itemDate >= new Date(dates.start) && itemDate <= new Date(dates.end);
      });

      dateRangeFilteredData = covariates
        ? dateRangeFilteredData
        : dateRangeFilteredData.filter((item: any) => item.name !== 'Covariates');

      switch (investment) {
        case 'revenue':
          chartData = cptbAreaData({
            fiscalCal: {},
            data: dateRangeFilteredData,
            data1Key: 'adjusted_outcome_to_observed',
            groupBy,
            dateGrouping,
            disabledPlatform,
            relative: absoluteOrRelative === 'Relative',
            companyEqualsTest
          });
          break;
        case 'costPerAcquisition':
          chartData = cptbAreaData({
            fiscalCal: {},
            data: dateRangeFilteredData,
            data1Key: 'raw_spend',
            groupBy,
            dateGrouping,
            data2Key: 'predicted_outcome',
            disabledPlatform,
            companyEqualsTest,
            // Defer the divide until after period roll-up so weekly/monthly/quarterly
            // CPA = sum_period(spend) / sum_period(effect) rather than sum_period(daily_cpa).
            // Cutoff checks spend (data1Key here, so cutoffMinField: 'data1') to suppress
            // lag-induced outliers on near-zero-spend periods (DEV-21247).
            roasCutoffMin: MIN_PERIOD_SPEND_FOR_ROAS,
            cutoffMinField: 'data1'
          });
          break;
        case 'roas':
          chartData = cptbAreaData({
            fiscalCal: {},
            data: dateRangeFilteredData,
            data1Key: 'predicted_outcome',
            groupBy,
            dateGrouping,
            data2Key: 'raw_spend',
            disabledPlatform,
            companyEqualsTest,
            // Defer the divide until after period roll-up so weekly/monthly/quarterly
            // ROAS = sum_period(effect) / sum_period(spend) rather than sum_period(daily_roas).
            // Periods with spend below the cutoff are zeroed to suppress lag-induced
            // outliers (DEV-21247).
            roasCutoffMin: MIN_PERIOD_SPEND_FOR_ROAS
          });
          break;
        default: {
          // spend and effect
          chartData = cptbAreaData({
            fiscalCal: {},
            data: dateRangeFilteredData,
            data1Key: 'raw_spend',
            groupBy,
            dateGrouping,
            disabledPlatform,
            relative: absoluteOrRelative === 'Relative',
            companyEqualsTest
          });
          const ac2Data = cptbAreaData({
            fiscalCal: {},
            data: dateRangeFilteredData,
            data1Key: 'adjusted_outcome_to_observed',
            groupBy,
            dateGrouping,
            disabledPlatform,
            relative: absoluteOrRelative === 'Relative',
            companyEqualsTest
          });
          setAreaChart2Data(ac2Data);
          break;
        }
      }

      const snapData = cptbSnapShotData({
        data: dateRangeFilteredData,
        groupBy,
        type: investment,
        disabledPlatform,
        companyEqualsTest,
        conversionPrettyName: CONVERSION_PRETTY_NAME[kpiType],
        kpiType
      });

      if (maxCptbDate) {
        const TTMsDate = Dfns.subYears(1)(maxCptbDate);

        let ttmFilteredData = cptbData.filter((item: any) => {
          const itemDate = new Date(item.date);
          return itemDate >= TTMsDate && itemDate <= maxCptbDate;
        });

        ttmFilteredData = covariates
          ? ttmFilteredData
          : ttmFilteredData.filter((item: any) => item.name !== 'Covariates');

        const TTMOverallRoasCpa = calculateTTMOverallRoasCpa(ttmFilteredData, kpiType);
        setOverallRoasCpa(TTMOverallRoasCpa);

        snapData.barData = snapShotDataSort(snapData.barData, snapChartSortValue);
        setSnapChartData(snapData);
        setAreaChartData(chartData);
      }
    }
    return undefined;
  }, [
    absoluteOrRelative,
    covariates,
    cptbData,
    dates,
    dateGrouping,
    disabledPlatform,
    investment,
    kpiType,
    groupBy,
    snapChartSortValue,
    company,
    companyEqualsTest,
    setOverallRoasCpa,
    maxCptbDate
  ]);

  useEffect(() => {
    setInvestment('spendAndEffectShare');
    setSnapChartSortValue('Spend: High to low');
  }, [kpiType]);

  // if kpiType is not revenue, remove roas
  const buildInvestmentOptions = () =>
    INVESTMENT_OPTIONS.filter(
      item =>
        !(kpiType !== 'revenue' && item.value === 'roas') &&
        !(kpiType === 'revenue' && item.value === 'costPerAcquisition')
    );

  const revenueAbsoluteRelativeFormatter = useCallback(
    (val: number) => {
      if (absoluteOrRelative === 'Absolute') {
        if (kpiType === 'revenue') {
          return val < 1000
            ? CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)
            : CONVERSION_FORMATTER_TYPE[kpiType].format(val, 1);
        }
        return CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0);
      }
      return absoluteOrRelative === 'Relative' ? `${val.toFixed(0)}%` : '';
    },
    [absoluteOrRelative, kpiType]
  );
  const spendAbsoluteRelativeFormatter = useCallback(
    (val: number) => {
      if (absoluteOrRelative === 'Absolute') {
        return val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1);
      }
      return absoluteOrRelative === 'Relative' ? `${val.toFixed(0)}%` : '';
    },
    [absoluteOrRelative]
  );

  // Dates currently render uniformly regardless of grouping. Kept as a single seam so a future
  // grouping-aware format can be introduced here (and moved to a util) in one place.
  const xAxisTickFormatter = useCallback((val: string) => dateToMddyy(val), []);

  const buildRightChart = () => {
    switch (investment) {
      case 'revenue':
        return (
          <AreaChart
            areas={areaChartData.areas}
            data={areaChartData.newD}
            dateGrouping={dateGrouping}
            hardCodedColorName={HARD_CODED_COLOR_NAME}
            hardCodedColor={HARD_CODED_COLOR}
            incrementality
            groupedAreas={areaChartData.groupedAreas}
            tooltipFormatter={val => revenueAbsoluteRelativeFormatter(val)}
            usePercentageYAxis={absoluteOrRelative === 'Relative'}
            xAxisDataKey='date'
            xAxisTickFormatter={val => xAxisTickFormatter(val)}
            yAxisTickFormatter={val => `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}`}
          />
        );
      case 'costPerAcquisition':
        return (
          <LineChart
            data={areaChartData?.newD}
            dateGrouping={dateGrouping}
            colorMappings={[{ name: HARD_CODED_COLOR_NAME, color: HARD_CODED_COLOR }]}
            groupedAreas={areaChartData.groupedAreas}
            lineDataKeys={areaChartData?.areas}
            tooltipLabelFormatter={val => (val ? capitalizeWords(val) : val)}
            tooltipFormatter={val => {
              if (!val) {
                return val;
              }

              return val > 15 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 2);
            }}
            useLeftYAxis
            xAxisDataKey='date'
            xAxisTickFormatter={val => xAxisTickFormatter(val)}
            yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
          />
        );
      case 'roas':
        return (
          <LineChart
            data={areaChartData?.newD}
            dateGrouping={dateGrouping}
            colorMappings={[{ name: HARD_CODED_COLOR_NAME, color: HARD_CODED_COLOR }]}
            groupedAreas={areaChartData.groupedAreas}
            lineDataKeys={areaChartData?.areas}
            tooltipLabelFormatter={val => (val ? capitalizeWords(val) : val)}
            tooltipFormatter={val => (val ? `${val.toFixed(2)}` : val)}
            tooltipShape='line'
            useLeftYAxis
            xAxisDataKey='date'
            xAxisTickFormatter={val => xAxisTickFormatter(val)}
          />
        );
      default:
        // spend and effect
        return (
          <div className='cptb-stacked-chart-wrapper'>
            <StackedAreaChart
              areas={areaChartData.areas}
              chart1Title='Spend'
              chart2Title={`Effect (${CONVERSION_PRETTY_NAME[kpiType]})`}
              data1={areaChartData.newD}
              data2={areaChart2Data.newD}
              dateGrouping={dateGrouping}
              groupedAreas={areaChartData.groupedAreas}
              hardCodedColorName={HARD_CODED_COLOR_NAME}
              hardCodedColor={HARD_CODED_COLOR}
              xAxisDataKey='date'
              tooltipFormatterTop={val => spendAbsoluteRelativeFormatter(val)}
              tooltipFormatterBottom={val => revenueAbsoluteRelativeFormatter(val)}
              usePercentageYAxis={absoluteOrRelative === 'Relative'}
              xAxisTickFormatter={val => xAxisTickFormatter(val)}
              yAxisTickFormatter1={val => currencyFormatter.format(val, 0)}
              yAxisTickFormatter2={val => `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}`}
            />
          </div>
        );
    }
  };

  const rightChartTitle = useCallback(() => {
    const option = INVESTMENT_OPTIONS.find(item => item.value === investment);
    return option?.title;
  }, [investment, INVESTMENT_OPTIONS]);

  const snapChartFormatter = useCallback(
    (value: number | null) => {
      switch (investment) {
        case 'costPerAcquisition':
          return value === null ? '--' : formatMoney(value);
        case 'roas':
          return value === null ? '--' : value.toFixed(2);
        case 'revenue':
        default:
          return value === null ? '--' : `${value.toFixed(0)}%`;
      }
    },
    [investment]
  );

  const buildSnapshotHeader = useCallback(() => {
    switch (investment) {
      case 'costPerAcquisition':
      case 'roas':
        return `Overall ${investment === 'roas' ? 'ROAS' : 'CPA'} ${snapChartData.titleData}`;
      default:
        return undefined;
    }
  }, [investment, snapChartData.titleData]);

  const excelDownloadCPTB = useCallback(() => {
    exportToExcel(cptbData, 'CPTB_Data');
  }, [cptbData]);

  const pngDownloadRight = useCallback(async () => {
    await downloadPNG('.cptb .right .chartContainer', `${rightChartTitle()}`);
  }, [rightChartTitle]);

  return (
    <WidgetContainer
      footer={<IncrementalityNote />}
      collapsible
      header={
        <div className='cptb-widget-dropdown-wrapper'>
          <span>How well did our investments do based on </span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            onChange={option => {
              setSnapChartSortValue(DEFAULT_SORT_SELECTIONS[option]);
              setInvestment(option);
            }}
            options={buildInvestmentOptions()}
            type={DropdownToggleType.PAGE_TITLE}
            value={investment}
          />
          <span>?</span>
        </div>
      }
      rightActions={
        <>
          <div className='cptb-covariates-toggle'>
            <ToggleSwitch
              design={Design.BRAND}
              size={Size.LG}
              label='Covariates'
              checked={covariates}
              onChange={() => setCovariates(!covariates)}
              infoTooltipText='An independent variable that can influence the outcome of a given statistical trial, but which is not of direct interest.'
            />
          </div>
          <DatePicker
            range={dates}
            isOutsideRange={date => {
              const range = getDefaultDates(cptbData);
              return new Date(date) < new Date(range.start) || new Date(date) > new Date(range.end);
            }}
            onChange={({ start, end }: DateRange) => {
              setDates({ start, end });
            }}
          />
        </>
      }
      subHeader={WIDGET_SUBHEADER[investment]}
    >
      <div className='cptb'>
        <LeftPanel
          showActualizedData={showActualizedData}
          investment={investment}
          twoStageBarData={twoStageBarData}
          snapChartData={snapChartData}
          spendAndEffectSortOptions={SPEND_AND_EFFECT_SORT_OPTIONS}
          snapChartSortValue={snapChartSortValue}
          setSnapChartSortValue={setSnapChartSortValue}
          snapChartTitle={SNAPCHART_TITLE[investment]}
          snapChartFormatter={snapChartFormatter}
          snapshotHeader={buildSnapshotHeader()}
          exportFormattedData={exportFormattedData}
          kpiType={kpiType}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          groupByOptions={groupByOptions}
          modelDate={modelDate}
          toggleLabel={toggleLabel}
          setToggleLabel={setToggleLabel}
        />
        <RightPanel
          dateGrouping={dateGrouping}
          setDateGrouping={setDateGrouping}
          chartTitle={rightChartTitle()}
          investment={investment}
          absoluteOrRelative={absoluteOrRelative}
          setAbsoluteOrRelative={setAbsoluteOrRelative}
          excelDownloadCPTB={excelDownloadCPTB}
          pngDownloadRight={pngDownloadRight}
          areaChartData={areaChartData}
          renderRightChart={buildRightChart}
        />
      </div>
    </WidgetContainer>
  );
};

const IncrementalityNote = () => (
  <div className='mmm-widget-bottom-wrapper'>
    <IncrementalityIcon className='incrementality-icon-bottom' />
    <div>
      Channels marked with a green beaker use informative priors based on incrementality studies.
      Incrementality testing is strongly recommended, and we encourage you to contact your team to
      review options.
    </div>
  </div>
);

interface LeftPanelProps {
  showActualizedData: boolean;
  investment: string;
  twoStageBarData: any[] | undefined;
  snapChartData: any;
  spendAndEffectSortOptions: readonly { value: string }[];
  snapChartSortValue: string;
  setSnapChartSortValue: Dispatch<SetStateAction<string>>;
  snapChartTitle: string;
  snapChartFormatter: (value: number | null) => string;
  snapshotHeader: string | undefined;
  exportFormattedData: any[];
  kpiType: string;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  modelDate: string;
  toggleLabel: boolean;
  setToggleLabel: Dispatch<SetStateAction<boolean>>;
}

const LeftPanel = ({
  showActualizedData,
  investment,
  twoStageBarData,
  snapChartData,
  spendAndEffectSortOptions,
  snapChartSortValue,
  setSnapChartSortValue,
  snapChartTitle,
  snapChartFormatter,
  snapshotHeader,
  exportFormattedData,
  kpiType,
  groupBy,
  setGroupBy,
  groupByOptions,
  modelDate,
  toggleLabel,
  setToggleLabel
}: LeftPanelProps) => {
  const showToggleLabel = investment === 'revenue' || investment === 'spendAndEffectShare';

  return (
    <div className='left'>
      {showActualizedData && investment === 'spendAndEffectShare'
        ? twoStageBarData && (
            <TwoStageSpendandEffectContainer
              colorOverrides={{ Covariates: HARD_CODED_COLOR }}
              twoStageData={twoStageBarData}
              dropdownOptions={spendAndEffectSortOptions}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              title={snapChartTitle}
              valueFormatter={snapChartFormatter}
              exportFormattedData={exportFormattedData}
              kpiType={kpiType}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              groupByOptions={groupByOptions}
              sortDropdownValue={snapChartSortValue}
              setSortDropdownValue={setSnapChartSortValue}
              modelDate={modelDate}
            />
          )
        : !R.isEmpty(snapChartData) && (
            <SnapshotChart
              colorOverrides={{ Covariates: HARD_CODED_COLOR }}
              data={snapChartData.barData}
              dropdownOptions={
                investment === 'spendAndEffectShare'
                  ? spendAndEffectSortOptions
                  : DEFAULT_SORT_OPTIONS
              }
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              doubleBarChart={investment === 'spendAndEffectShare'}
              header={snapshotHeader}
              setToggleLabel={showToggleLabel ? setToggleLabel : undefined}
              title={snapChartTitle}
              toggleLabel={showToggleLabel ? toggleLabel : true}
              toggleSwitchLabel={kpiType === 'revenue' ? '$' : 'Volume'}
              valueFormatter={snapChartFormatter}
              exportFormattedData={exportFormattedData}
            />
          )}
    </div>
  );
};

interface RightPanelProps {
  dateGrouping: string;
  setDateGrouping: Dispatch<SetStateAction<string>>;
  chartTitle: string | undefined;
  investment: string;
  absoluteOrRelative: string;
  setAbsoluteOrRelative: Dispatch<SetStateAction<string>>;
  excelDownloadCPTB: () => void;
  pngDownloadRight: () => void;
  areaChartData: any;
  renderRightChart: () => ReactNode;
}

const RightPanel = ({
  dateGrouping,
  setDateGrouping,
  chartTitle,
  investment,
  absoluteOrRelative,
  setAbsoluteOrRelative,
  excelDownloadCPTB,
  pngDownloadRight,
  areaChartData,
  renderRightChart
}: RightPanelProps) => (
  <div className='right'>
    <ChartContainer
      title={
        <>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            type={DropdownToggleType.WIDGET_TITLE}
            value={dateGrouping}
            options={DATE_GROUPING_OPTIONS}
            onChange={option => setDateGrouping(option)}
          />
          <div>{chartTitle}</div>
        </>
      }
      rightActions={
        <>
          {(investment === 'revenue' || investment === 'spendAndEffectShare') && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              stretch={StretchMode.FIT}
              value={absoluteOrRelative}
              options={[{ value: 'Absolute' }, { value: 'Relative' }]}
              onChange={option => setAbsoluteOrRelative(option)}
            />
          )}
          <DownloadDropdown onClickOptions={[excelDownloadCPTB, pngDownloadRight]} />
        </>
      }
    >
      {!R.isEmpty(areaChartData) && renderRightChart()}
    </ChartContainer>
  </div>
);
